import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import ConfirmAction from "../../../Components/Utils/ConfirmAction/ConfirmAction";
import {
  ClientHardDeleteReport,
  ClientListItem,
  PendingAuth0Delete,
  PendingAuth0DeleteReport,
  deletePendingAuth0Users,
  getClientList,
  hardDeleteClient,
  listPendingAuth0Deletes,
  wipeReportFromError,
} from "../../../API/ClientHardDelete/ClientHardDelete";
import styles from "./DeleteClient.module.css";

type ActionKind = "data" | "auth0";

const namesMatch = (typed: string, clientName: string): boolean =>
  typed.trim().length > 0 && typed.trim() === clientName.trim();

const identityLabel = (user: { sub: string; email: string }): string =>
  user.email ? `${user.email}` : user.sub;

const DeleteClient = () => {
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ClientListItem | null>(null);
  const [removed, setRemoved] = useState<ClientListItem | null>(null);
  const [pending, setPending] = useState<PendingAuth0Delete[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [action, setAction] = useState<ActionKind | null>(null);
  const [typedName, setTypedName] = useState("");
  const [busy, setBusy] = useState(false);
  const [wipeReport, setWipeReport] = useState<ClientHardDeleteReport | null>(null);
  const [auth0Report, setAuth0Report] = useState<PendingAuth0DeleteReport | null>(null);

  const focus = selected ?? removed;

  const visibleClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter((client) => client.name.toLowerCase().includes(term));
  }, [clients, search]);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      setClients(await getClientList());
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const refreshPending = useCallback(async (clientId: string) => {
    setPendingLoading(true);
    try {
      setPending(await listPendingAuth0Deletes(clientId));
    } catch {
      setPending([]);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  useEffect(() => {
    const clientId = focus?.id;
    if (!clientId) {
      setPending([]);
      return;
    }

    let cancelled = false;
    setPendingLoading(true);
    listPendingAuth0Deletes(clientId)
      .then((rows) => {
        if (!cancelled) setPending(rows);
      })
      .catch(() => {
        if (!cancelled) setPending([]);
      })
      .finally(() => {
        if (!cancelled) setPendingLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [focus?.id]);

  const closeDialog = () => {
    setAction(null);
    setTypedName("");
  };

  const chooseClient = (client: ClientListItem) => {
    setSelected(client);
    setRemoved(null);
    setWipeReport(null);
    setAuth0Report(null);
  };

  const deleteData = async () => {
    if (!focus || !namesMatch(typedName, focus.name)) return;
    setBusy(true);
    try {
      const report = await hardDeleteClient(focus.id);
      setWipeReport(report);
      setRemoved(focus);
      setSelected(null);
      setAuth0Report(null);
      toast.success(`${focus.name} was deleted. Auth0 accounts were left in place.`);
      await loadClients();
      await refreshPending(focus.id);
    } catch (error) {
      const partial = wipeReportFromError(error);
      if (partial) setWipeReport(partial);
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const deleteAuth0 = async () => {
    if (!focus || !namesMatch(typedName, focus.name)) return;
    setBusy(true);
    try {
      const report = await deletePendingAuth0Users(focus.id);
      setAuth0Report(report);
      toast.success(`Auth0 deletion finished for ${focus.name}.`);
      await refreshPending(focus.id);
    } finally {
      setBusy(false);
    }
  };

  const confirmed = focus ? namesMatch(typedName, focus.name) : false;
  const auth0Disabled = !focus || pendingLoading || pending.length === 0 || busy;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Mesma Portal</p>
          <h1>Delete client</h1>
          <p className={styles.intro}>
            Remove one client's data from Mesma. Auth0 accounts for users who belonged only to that
            client are queued, and deleted only when you use the second button.
          </p>
        </div>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchBar value={search} onChange={setSearch} type="" />
          </div>
          <span className={styles.statPill}>
            <strong>{clients.length}</strong> active client{clients.length === 1 ? "" : "s"}
          </span>
        </div>
      </header>

      <section className={styles.workspace}>
        <div className={styles.listPane}>
          <div className={styles.panelHeader}>
            <h3>Clients</h3>
            <p className={styles.panelSub}>
              {loading ? "Loading…" : `${visibleClients.length} shown`}
            </p>
          </div>
          {visibleClients.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{loading ? "Loading clients…" : "No clients match your search."}</p>
            </div>
          ) : (
            <ul className={styles.clientList}>
              {visibleClients.map((client) => (
                <li key={client.id}>
                  <button
                    type="button"
                    className={`${styles.clientButton} ${
                      focus?.id === client.id ? styles.clientButtonActive : ""
                    }`}
                    onClick={() => chooseClient(client)}
                  >
                    <span className={styles.clientTitle}>{client.name || "Unnamed client"}</span>
                    <span className={styles.clientMeta}>{client.id}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.detailPane}>
          {!focus ? (
            <div className={styles.emptyState}>
              <p>Select a client to review the two deletion steps.</p>
            </div>
          ) : (
            <>
              <div>
                <h2 className={styles.detailTitle}>{focus.name || "Unnamed client"}</h2>
                <p className={styles.detailId}>{focus.id}</p>
              </div>
              <p className={styles.warning}>
                Deleting client data is permanent and cannot be undone. It removes this client's
                records only. Auth0 logins stay until you delete them separately.
              </p>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.btnDanger}
                  disabled={busy}
                  onClick={() => setAction("data")}
                >
                  Delete client data
                </button>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  disabled={auth0Disabled}
                  onClick={() => setAction("auth0")}
                >
                  {pendingLoading
                    ? "Checking Auth0 queue…"
                    : `Delete Auth0 users (${pending.length})`}
                </button>
              </div>

              <section className={styles.section}>
                <h3>Queued Auth0 users</h3>
                {pendingLoading ? (
                  <p className={styles.panelSub}>Checking the queue…</p>
                ) : pending.length === 0 ? (
                  <p className={styles.panelSub}>No Auth0 users are waiting for this client.</p>
                ) : (
                  <ul className={styles.identityList}>
                    {pending.map((user) => (
                      <li key={user.id || user.sub}>
                        {identityLabel(user)}
                        <span>{user.sub}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {removed && wipeReport && (
                <section className={styles.section}>
                  <h3>Data deletion</h3>
                  <p className={styles.panelSub}>
                    {wipeReport.failedPhase
                      ? `Stopped during ${wipeReport.failedPhase}.`
                      : `${focus.name} has been removed from the client list.`}
                    {` ${wipeReport.queuedAuth0Users.length} Auth0 user${
                      wipeReport.queuedAuth0Users.length === 1 ? "" : "s"
                    } queued.`}
                  </p>
                </section>
              )}

              {auth0Report && (
                <section className={styles.section}>
                  <h3>Auth0 result</h3>
                  <ul className={styles.identityList}>
                    {auth0Report.deleted.map((user) => (
                      <li key={`deleted-${user.sub}`}>
                        Deleted {identityLabel(user)}
                        <span>{user.sub}</span>
                      </li>
                    ))}
                    {auth0Report.alreadyAbsent.map((user) => (
                      <li key={`absent-${user.sub}`}>
                        Already absent {identityLabel(user)}
                        <span>{user.sub}</span>
                      </li>
                    ))}
                    {auth0Report.failed.map((failure) => (
                      <li key={`failed-${failure.user.sub}`} className={styles.failure}>
                        Failed {identityLabel(failure.user)}
                        <span>{failure.error || failure.user.sub}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </section>

      <ConfirmAction
        isVisible={action !== null && focus !== null}
        onClose={closeDialog}
        clickHandler={action === "auth0" ? deleteAuth0 : deleteData}
        title={action === "auth0" ? `Delete Auth0 users for ${focus?.name}?` : `Delete ${focus?.name}?`}
        message={
          action === "auth0"
            ? `This permanently deletes the queued Auth0 accounts for ${focus?.name}. Accounts that fail stay in the queue.`
            : `This permanently deletes ${focus?.name} and its data. Other clients are not affected. Auth0 accounts are not deleted yet.`
        }
        confirmText={action === "auth0" ? "Delete Auth0 users" : "Delete client data"}
        severity="danger"
        loading={busy}
        confirmDisabled={!confirmed}
      >
        <label className={styles.nameField}>
          Type {focus?.name} to confirm
          <input
            value={typedName}
            onChange={(event) => setTypedName(event.target.value)}
            autoComplete="off"
            aria-label="Client name confirmation"
          />
        </label>
        <p className={styles.nameHint}>
          {confirmed ? "Name matches. You can confirm." : "The confirm button stays off until the name matches."}
        </p>
      </ConfirmAction>
    </div>
  );
};

export default DeleteClient;
